"use client";

import { motion, type SpringOptions, useMotionValue, useSpring } from "motion/react";
import * as React from "react";

import { cn } from "@/lib/utils";

type BubbleBackgroundProps = React.ComponentProps<"div"> & {
	interactive?: boolean;
	transition?: SpringOptions;
	colors?: {
		first: string;
		second: string;
		third: string;
		fourth: string;
		fifth: string;
		sixth: string;
		seventh: string;
		eighth: string;
		ninth: string;
	};
};

function BubbleBackground({
	ref,
	className,
	children,
	interactive = false,
	transition = { stiffness: 100, damping: 20 },
	colors = {
		first: "18,113,255",
		second: "221,74,255",
		third: "0,220,255",
		fourth: "200,50,50",
		fifth: "180,180,50",
		sixth: "140,100,255",
		seventh: "100,150,255",
		eighth: "150,200,255",
		ninth: "180,220,255",
	},
	...props
}: BubbleBackgroundProps) {
	const id = React.useId();
	const filterId = `goo-${id}`;
	const containerRef = React.useRef<HTMLDivElement>(null);
	React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

	const mouseX = useMotionValue(0);
	const mouseY = useMotionValue(0);
	const springX = useSpring(mouseX, transition);
	const springY = useSpring(mouseY, transition);

	React.useEffect(() => {
		if (!interactive) return;

		const currentContainer = containerRef.current;
		if (!currentContainer) return;

		const handleMouseMove = (e: MouseEvent) => {
			const rect = currentContainer.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;
			mouseX.set(e.clientX - centerX);
			mouseY.set(e.clientY - centerY);
		};

		currentContainer?.addEventListener("mousemove", handleMouseMove);
		return () => currentContainer?.removeEventListener("mousemove", handleMouseMove);
	}, [interactive, mouseX, mouseY]);

	return (
		<div
			ref={containerRef}
			data-slot="bubble-background"
			className={cn("relative size-full overflow-hidden", className)}
			{...props}
		>
			<style>
				{`
            :root {
              --first-color: ${colors.first};
              --second-color: ${colors.second};
              --third-color: ${colors.third};
              --fourth-color: ${colors.fourth};
              --fifth-color: ${colors.fifth};
              --sixth-color: ${colors.sixth};
              --seventh-color: ${colors.seventh};
              --eighth-color: ${colors.eighth};
              --ninth-color: ${colors.ninth};
            }
          `}
			</style>

			<svg xmlns="http://www.w3.org/2000/svg" className="absolute top-0 left-0 w-0 h-0">
				<title>Bubble gradient filter</title>
				<defs>
					<filter id={filterId}>
						<feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
						<feColorMatrix
							in="blur"
							mode="matrix"
							values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
							result="goo"
						/>
						<feBlend in="SourceGraphic" in2="goo" />
					</filter>
				</defs>
			</svg>

			<div className="absolute inset-0" style={{ filter: `url(#${filterId}) blur(40px)` }}>
				{/* Bubble 1 - Vertical movement (faster) */}
				<motion.div
					className="absolute rounded-full size-[80%] top-[10%] left-[10%] mix-blend-hard-light bg-[radial-gradient(circle_at_center,rgba(var(--first-color),0.8)_0%,rgba(var(--first-color),0)_50%)]"
					animate={{ y: [-50, 50, -50] }}
					transition={{ duration: 18, ease: "easeInOut", repeat: Infinity }}
				/>

				{/* Bubble 2 - Rotation (faster) */}
				<motion.div
					className="absolute inset-0 flex justify-center items-center origin-[calc(50%-400px)]"
					animate={{ rotate: 360 }}
					transition={{
						duration: 12,
						ease: "linear",
						repeat: Infinity,
						repeatType: "loop",
					}}
				>
					<div className="rounded-full size-[80%] top-[10%] left-[10%] mix-blend-hard-light bg-[radial-gradient(circle_at_center,rgba(var(--second-color),0.8)_0%,rgba(var(--second-color),0)_50%)]" />
				</motion.div>

				{/* Bubble 3 - Rotation with offset (faster) */}
				<motion.div
					className="absolute inset-0 flex justify-center items-center origin-[calc(50%+400px)]"
					animate={{ rotate: 360 }}
					transition={{ duration: 24, ease: "linear", repeat: Infinity }}
				>
					<div className="absolute rounded-full size-[80%] bg-[radial-gradient(circle_at_center,rgba(var(--third-color),0.8)_0%,rgba(var(--third-color),0)_50%)] mix-blend-hard-light top-[calc(50%+200px)] left-[calc(50%-500px)]" />
				</motion.div>

				{/* Bubble 4 - Horizontal movement (faster) */}
				<motion.div
					className="absolute rounded-full size-[80%] top-[10%] left-[10%] mix-blend-hard-light bg-[radial-gradient(circle_at_center,rgba(var(--fourth-color),0.8)_0%,rgba(var(--fourth-color),0)_50%)] opacity-70"
					animate={{ x: [-50, 50, -50] }}
					transition={{ duration: 24, ease: "easeInOut", repeat: Infinity }}
				/>

				{/* Bubble 5 - Large rotation (faster) */}
				<motion.div
					className="absolute inset-0 flex justify-center items-center origin-[calc(50%_-_800px)_calc(50%_+_200px)]"
					animate={{ rotate: 360 }}
					transition={{ duration: 12, ease: "linear", repeat: Infinity }}
				>
					<div className="absolute rounded-full size-[160%] mix-blend-hard-light bg-[radial-gradient(circle_at_center,rgba(var(--fifth-color),0.8)_0%,rgba(var(--fifth-color),0)_50%)] top-[calc(50%-80%)] left-[calc(50%-80%)]" />
				</motion.div>

				{/* Bubble 6 - Interactive (mouse follower) */}
				{interactive && (
					<motion.div
						className="absolute rounded-full size-full mix-blend-hard-light bg-[radial-gradient(circle_at_center,rgba(var(--sixth-color),0.8)_0%,rgba(var(--sixth-color),0)_50%)] opacity-70"
						style={{
							x: springX,
							y: springY,
						}}
					/>
				)}

				{/* Bubble 7 - Diagonal movement (NEW) */}
				<motion.div
					className="absolute rounded-full size-[70%] top-[20%] left-[20%] mix-blend-hard-light bg-[radial-gradient(circle_at_center,rgba(var(--seventh-color),0.8)_0%,rgba(var(--seventh-color),0)_50%)] opacity-80"
					animate={{ x: [-40, 40, -40], y: [-30, 30, -30] }}
					transition={{ duration: 16, ease: "easeInOut", repeat: Infinity }}
				/>

				{/* Bubble 8 - Circular orbit with offset (NEW) */}
				<motion.div
					className="absolute inset-0 flex justify-center items-center origin-[calc(50%+300px)_calc(50%-200px)]"
					animate={{ rotate: -360 }}
					transition={{ duration: 14, ease: "linear", repeat: Infinity }}
				>
					<div className="absolute rounded-full size-[90%] mix-blend-hard-light bg-[radial-gradient(circle_at_center,rgba(var(--eighth-color),0.8)_0%,rgba(var(--eighth-color),0)_50%)] top-[calc(50%-100px)] left-[calc(50%+150px)]" />
				</motion.div>

				{/* Bubble 9 - Wave-like diagonal movement (NEW) */}
				<motion.div
					className="absolute rounded-full size-[75%] bottom-[15%] right-[15%] mix-blend-hard-light bg-[radial-gradient(circle_at_center,rgba(var(--ninth-color),0.8)_0%,rgba(var(--ninth-color),0)_50%)] opacity-75"
					animate={{ x: [30, -30, 30], y: [20, -40, 20] }}
					transition={{ duration: 20, ease: "easeInOut", repeat: Infinity }}
				/>
			</div>

			{children}
		</div>
	);
}

export { BubbleBackground, type BubbleBackgroundProps };
